import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ApiError } from '../api/http';
import {
  decideRoadmapProposal,
  deleteRoadmapComment,
  editRoadmapComment,
  fetchRoadmap,
  fetchRoadmapComments,
  postRoadmapComment,
  type CommentKind,
  type Decision,
  type RoadmapComment,
  type RoadmapItem,
  type RoadmapPage,
  type RoadmapStage,
  type RoadmapState,
  type ThreadTarget,
} from '../api/roadmap';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { Avatar } from './IdentityPanel';
import { useMeta } from './MetaContext';
import { roadmapUpdatedAt } from './roadmapUpdated';
import { PageLoading, PageProblem } from './PageStates';
import { Shell } from './Shell';

const MAX_BODY = 2000;
/** The stage of what a player never sees: drawn last, always closed, and left out of the overall figure. */
const UNDER_THE_HOOD = 'sota-el-capo';
/** The order the overall bar is cut in; the two states after these are "the rest". */
const BAR_STATES: RoadmapState[] = ['fet', 'en-proves', 'en-curs', 'comencat'];

type AdminList = 'review' | 'unfiled' | 'retired';

/** One list of messages on the page: an item's, a stage's, the page's own, or one of the administrator's. */
const keyOf = (target: ThreadTarget): string =>
  'item' in target
    ? `item:${target.item}`
    : 'stage' in target
      ? `stage:${target.stage}`
      : 'general' in target
        ? 'general'
        : 'review' in target
          ? 'review'
          : 'unfiled' in target
            ? 'unfiled'
            : 'retired';

const ADMIN_TARGETS: Record<AdminList, ThreadTarget> = {
  review: { review: true },
  unfiled: { unfiled: true },
  retired: { retired: true },
};

const stateOf = (state: string): RoadmapState =>
  state in ca.roadmapPage.states ? (state as RoadmapState) : 'mes-endavant';

function StateChip({ state }: { state: string }) {
  const known = stateOf(state);
  return <span className={`chip rm-state rm-${known}`}>{ca.roadmapPage.states[known]}</span>;
}

function countByState(items: RoadmapItem[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const item of items) counts[stateOf(item.state)] = (counts[stateOf(item.state)] ?? 0) + 1;
  return counts;
}

/** A bar cut by state: done, in tests, under way, started, and the rest. Nothing is drawn for a list with no item. */
function ProgressBar({ items, label }: { items: RoadmapItem[]; label: string }) {
  if (items.length === 0) return null;
  const counts = countByState(items);
  const rest = items.length - BAR_STATES.reduce((sum, s) => sum + (counts[s] ?? 0), 0);
  return (
    <div className="rm-bar" role="img" aria-label={label}>
      {BAR_STATES.map(
        (s) =>
          (counts[s] ?? 0) > 0 && (
            <span
              key={s}
              className={`rm-seg rm-${s}`}
              style={{ flexGrow: counts[s] }}
              title={`${ca.roadmapPage.states[s]}: ${counts[s]}`}
            />
          ),
      )}
      {rest > 0 && (
        <span
          className="rm-seg rm-rest"
          style={{ flexGrow: rest }}
          title={`${ca.roadmapPage.rest}: ${rest}`}
        />
      )}
    </div>
  );
}

function ago(iso: string, now: number): string {
  const t = ca.roadmapPage;
  const minutes = Math.floor((now - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return t.just;
  if (minutes < 60) return t.minutesAgo(minutes);
  if (minutes < 60 * 24) return t.hoursAgo(Math.floor(minutes / 60));
  const days = Math.floor(minutes / (60 * 24));
  if (days < 30) return t.daysAgo(days);
  return new Date(iso).toLocaleDateString('ca', { dateStyle: 'medium' });
}

const errorOf = (err: unknown): string =>
  err instanceof ApiError
    ? (ca.roadmapPage.errors[err.code] ?? ca.roadmapPage.errors.other)
    : ca.roadmapPage.errors.other;

interface Actions {
  admin: boolean;
  onEdit: (id: number, body: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onDecide: (id: number, decision: Decision | null, note: string, filed: boolean) => Promise<void>;
}

/** What only the administrator sees under a proposal: the decision, a way to take it back, and a private note. */
function AdminBox({ comment, actions }: { comment: RoadmapComment; actions: Actions }) {
  const t = ca.roadmapPage.admin;
  const held = comment.admin;
  // The note being typed is this box's own: an answer that arrives for another
  // click replaces the row, never the text in the field.
  const [note, setNote] = useState(held?.note ?? '');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!held) return null;

  async function decide(decision: Decision | null, filed: boolean, noted = false) {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      await actions.onDecide(comment.id, decision, note, filed);
      setSaved(noted);
    } catch (err) {
      setError(errorOf(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rm-adminbox" role="group" aria-label={t.only}>
      <span className="rm-only">
        <i className="bi bi-eye-slash" aria-hidden="true" /> {t.only}
      </span>
      {held.decision ? (
        <div className="row">
          <span className={`chip ${held.decision === 'ACCEPTED' ? 'ok' : 'bad'}`}>
            {t.decided[held.decision]}
          </span>
          {held.decision === 'ACCEPTED' && held.filed && <span className="chip">{t.filed}</span>}
          <button
            type="button"
            className="ghost small"
            disabled={busy}
            onClick={() => decide(null, false)}
          >
            <i className="bi bi-arrow-counterclockwise" aria-hidden="true" /> {t.undo}
          </button>
          {held.decision === 'ACCEPTED' && (
            <button
              type="button"
              className="ghost small"
              disabled={busy}
              onClick={() => decide('ACCEPTED', !held.filed)}
            >
              <i className="bi bi-file-earmark-check" aria-hidden="true" />{' '}
              {held.filed ? t.unmarkFiled : t.markFiled}
            </button>
          )}
        </div>
      ) : (
        <div className="row">
          <button
            type="button"
            className="small"
            disabled={busy}
            onClick={() => decide('ACCEPTED', false)}
          >
            <i className="bi bi-check-lg" aria-hidden="true" /> {t.accept}
          </button>
          <button
            type="button"
            className="ghost small"
            disabled={busy}
            onClick={() => decide('DENIED', false)}
          >
            <i className="bi bi-x-lg" aria-hidden="true" /> {t.deny}
          </button>
        </div>
      )}
      <label className="rm-note">
        {t.note}
        <textarea
          rows={2}
          maxLength={MAX_BODY}
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            setSaved(false);
          }}
        />
      </label>
      <div className="row">
        <button
          type="button"
          className="ghost small"
          disabled={busy || note === (held.note ?? '')}
          onClick={() => decide(held.decision, held.filed, true)}
        >
          {t.noteSave}
        </button>
        {saved && (
          <span className="muted small" role="status">
            {t.noteSaved}
          </span>
        )}
      </div>
      {error && (
        <p className="error small" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function Message({
  comment,
  actions,
  now,
  where,
}: {
  comment: RoadmapComment;
  actions: Actions;
  now: number;
  /** In the administrator's lists, what the message hangs from. */
  where?: string;
}) {
  const t = ca.roadmapPage;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = comment.author.displayName || comment.author.handle || t.someone;

  async function save() {
    setBusy(true);
    setError(null);
    try {
      await actions.onEdit(comment.id, draft);
      setEditing(false);
    } catch (err) {
      setError(errorOf(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(t.removeConfirm)) return;
    setBusy(true);
    setError(null);
    try {
      await actions.onDelete(comment.id);
    } catch (err) {
      setError(errorOf(err));
      setBusy(false);
    }
  }

  return (
    <li className="rm-msg">
      <Avatar url={comment.author.avatarUrl} name={name} size={28} />
      <div className="rm-msg-body">
        <div className="rm-msg-head">
          <strong>{name}</strong>
          <time className="muted small" dateTime={comment.createdAt}>
            {ago(comment.createdAt, now)}
          </time>
          {comment.editedAt && <span className="muted small">· {t.edited}</span>}
          {comment.kind === 'PROPOSAL' && <span className="chip warn">{t.proposal}</span>}
          {comment.accepted && (
            <span className="chip ok">
              <i className="bi bi-check2-circle" aria-hidden="true" /> {t.accepted}
            </span>
          )}
        </div>
        {where && <p className="muted small rm-where">{where}</p>}
        {editing ? (
          <div className="rm-compose">
            <textarea
              aria-label={t.edit}
              rows={3}
              maxLength={MAX_BODY}
              value={draft}
              readOnly={busy}
              onChange={(e) => setDraft(e.target.value)}
            />
            <div className="row">
              <button
                type="button"
                className="small"
                disabled={busy || draft.trim() === ''}
                onClick={save}
              >
                {t.save}
              </button>
              <button
                type="button"
                className="ghost small"
                disabled={busy}
                onClick={() => {
                  setEditing(false);
                  setDraft(comment.body);
                  setError(null);
                }}
              >
                {t.cancel}
              </button>
            </div>
          </div>
        ) : (
          <p className="rm-text">{comment.body}</p>
        )}
        {!editing && (comment.mine || actions.admin) && (
          <div className="rm-msg-actions">
            {comment.mine && (
              <button
                type="button"
                className="link small"
                disabled={busy}
                onClick={() => {
                  setDraft(comment.body);
                  setEditing(true);
                }}
              >
                <i className="bi bi-pencil" aria-hidden="true" /> {t.edit}
              </button>
            )}
            <button type="button" className="link small" disabled={busy} onClick={remove}>
              <i className="bi bi-trash3" aria-hidden="true" /> {t.remove}
            </button>
          </div>
        )}
        {error && (
          <p className="error small" role="alert">
            {error}
          </p>
        )}
        {actions.admin && comment.kind === 'PROPOSAL' && comment.admin && (
          <AdminBox comment={comment} actions={actions} />
        )}
      </div>
    </li>
  );
}

/** A textarea and its buttons. The text is the page's (kept per thread), so closing a thread loses nothing. */
function Composer({
  label,
  draft,
  sending,
  error,
  proposalOnly,
  onDraft,
  onSend,
}: {
  label: string;
  draft: string;
  sending: boolean;
  error: string | null;
  proposalOnly: boolean;
  onDraft: (text: string) => void;
  onSend: (kind: CommentKind) => void;
}) {
  const t = ca.roadmapPage;
  const empty = draft.trim() === '';
  return (
    <div className="rm-compose">
      <textarea
        aria-label={label}
        placeholder={label}
        rows={3}
        maxLength={MAX_BODY}
        value={draft}
        readOnly={sending}
        onChange={(e) => onDraft(e.target.value)}
      />
      <div className="row">
        {proposalOnly ? (
          <button type="button" disabled={sending || empty} onClick={() => onSend('PROPOSAL')}>
            <i className="bi bi-lightbulb" aria-hidden="true" /> {t.proposeSend}
          </button>
        ) : (
          <>
            <button type="button" disabled={sending || empty} onClick={() => onSend('COMMENT')}>
              <i className="bi bi-chat" aria-hidden="true" /> {t.send}
            </button>
            <button
              type="button"
              className="ghost"
              disabled={sending || empty}
              onClick={() => onSend('PROPOSAL')}
            >
              <i className="bi bi-lightbulb" aria-hidden="true" /> {t.sendAsProposal}
            </button>
          </>
        )}
        {draft.length > MAX_BODY * 0.9 && (
          <span className="muted small">{t.counter(draft.length, MAX_BODY)}</span>
        )}
      </div>
      {error && (
        <p className="error small" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

interface ThreadState {
  list: RoadmapComment[] | null;
  failed: boolean;
}

/**
 * The roadmap, for anybody with an account: what is done, what is being done
 * and what comes, each point open to comments and to proposals.
 *
 * Everything typed and not yet sent lives here, one draft per thread, and
 * nothing the platform answers ever replaces it: an answer changes the one
 * message it is about, found by its id, and a list that arrives late for a
 * thread that was asked again is dropped (memory:
 * a-save-that-answers-late-must-not-overwrite-the-page).
 */
export function Roadmap() {
  const t = ca.roadmapPage;
  const { me } = useAuth();
  const { meta } = useMeta();
  const admin = me.roles.includes('ADMIN');
  const [page, setPage] = useState<RoadmapPage | null>(null);
  const [failed, setFailed] = useState(false);
  const [openStages, setOpenStages] = useState<Record<string, boolean>>({});
  const [openThreads, setOpenThreads] = useState<Record<string, boolean>>({});
  const [threads, setThreads] = useState<Record<string, ThreadState>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [sending, setSending] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [adminList, setAdminList] = useState<AdminList | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const pageAsked = useRef(0);
  /** Why the document did not arrive: no connection, or the platform's answer. */
  const [pageError, setPageError] = useState<unknown>(null);
  const threadAsked = useRef<Record<string, number>>({});
  const stagesSet = useRef(false);

  /** The document and its counts. Only the answer to the latest request is kept. */
  const loadPage = useCallback(() => {
    const asked = ++pageAsked.current;
    return fetchRoadmap()
      .then((answer) => {
        if (asked !== pageAsked.current) return;
        setPage(answer);
        setFailed(false);
        setNow(Date.now());
        if (!stagesSet.current) {
          stagesSet.current = true;
          setOpenStages(
            Object.fromEntries(
              answer.stages.map((s) => [
                s.slug,
                s.slug !== UNDER_THE_HOOD &&
                  (stateOf(s.state) === 'en-curs' || stateOf(s.state) === 'en-proves'),
              ]),
            ),
          );
        }
      })
      .catch((err) => {
        if (asked !== pageAsked.current) return;
        setFailed(true);
        setPageError(err);
      });
  }, []);

  const loadThread = useCallback((target: ThreadTarget) => {
    const key = keyOf(target);
    const asked = (threadAsked.current[key] ?? 0) + 1;
    threadAsked.current[key] = asked;
    return fetchRoadmapComments(target)
      .then((list) => {
        if (threadAsked.current[key] !== asked) return;
        setThreads((all) => ({ ...all, [key]: { list, failed: false } }));
      })
      .catch(() => {
        if (threadAsked.current[key] !== asked) return;
        setThreads((all) => ({ ...all, [key]: { list: all[key]?.list ?? null, failed: true } }));
      });
  }, []);

  useEffect(() => {
    void loadPage();
    void loadThread({ general: true });
  }, [loadPage, loadThread]);

  // A «Pendent» chip elsewhere leads here with `#<item id>`: open the stage that holds the item,
  // scroll to it and mark it (`.rm-item.target`). The browser's own anchor jump cannot, since
  // the page arrives from the platform after the first paint and its stages start closed.
  const { hash } = useLocation();
  const targetId = (() => {
    try {
      return decodeURIComponent(hash.slice(1));
    } catch {
      return '';
    }
  })();
  const targetStage = targetId
    ? page?.stages.find((s) => s.items.some((i) => i.id === targetId))
    : undefined;
  const targetSlug = targetStage?.slug;
  const scrolledTo = useRef('');
  useEffect(() => {
    if (targetSlug)
      setOpenStages((all) => (all[targetSlug] ? all : { ...all, [targetSlug]: true }));
  }, [targetSlug, targetId]);
  const targetOpen = !!targetSlug && !!openStages[targetSlug];
  useEffect(() => {
    if (!targetOpen || scrolledTo.current === targetId) return;
    scrolledTo.current = targetId;
    document.getElementById(targetId)?.scrollIntoView?.({ block: 'center' });
  }, [targetOpen, targetId]);

  function toggleThread(target: ThreadTarget) {
    const key = keyOf(target);
    const opening = !openThreads[key];
    setOpenThreads((all) => ({ ...all, [key]: opening }));
    if (opening) void loadThread(target);
  }

  function toggleAdminList(which: AdminList) {
    const opening = adminList !== which;
    setAdminList(opening ? which : null);
    if (opening) void loadThread(ADMIN_TARGETS[which]);
  }

  /** One message changed: replaced where it stands, in every list that holds it. */
  function replace(changed: RoadmapComment) {
    setThreads((all) =>
      Object.fromEntries(
        Object.entries(all).map(([key, thread]) => [
          key,
          thread.list
            ? { ...thread, list: thread.list.map((c) => (c.id === changed.id ? changed : c)) }
            : thread,
        ]),
      ),
    );
  }

  async function send(target: ThreadTarget, kind: CommentKind) {
    const key = keyOf(target);
    const body = (drafts[key] ?? '').trim();
    if (body === '' || sending[key]) return;
    setSending((all) => ({ ...all, [key]: true }));
    setErrors((all) => ({ ...all, [key]: null }));
    try {
      const posted = await postRoadmapComment({
        ...('item' in target ? { itemId: target.item } : {}),
        ...('stage' in target ? { stageSlug: target.stage } : {}),
        kind,
        body,
      });
      // The field is read-only while this is on its way, so what is cleared is what was sent.
      setDrafts((all) => ({ ...all, [key]: '' }));
      setThreads((all) => {
        const list = all[key]?.list ?? [];
        return {
          ...all,
          [key]: {
            list: list.some((c) => c.id === posted.id) ? list : [...list, posted],
            failed: false,
          },
        };
      });
      void loadPage();
    } catch (err) {
      setErrors((all) => ({ ...all, [key]: errorOf(err) }));
    } finally {
      setSending((all) => ({ ...all, [key]: false }));
    }
  }

  const actions: Actions = {
    admin,
    onEdit: async (id, body) => {
      replace(await editRoadmapComment(id, body.trim()));
    },
    onDelete: async (id) => {
      await deleteRoadmapComment(id);
      setThreads((all) =>
        Object.fromEntries(
          Object.entries(all).map(([key, thread]) => [
            key,
            thread.list ? { ...thread, list: thread.list.filter((c) => c.id !== id) } : thread,
          ]),
        ),
      );
      void loadPage();
    },
    onDecide: async (id, decision, note, filed) => {
      replace(await decideRoadmapProposal(id, decision, note, filed));
      void loadPage();
    },
  };

  function thread(target: ThreadTarget, label: string, proposalOnly: boolean, emptyText?: string) {
    const key = keyOf(target);
    const state = threads[key];
    return (
      <div className="rm-thread" role="region" aria-label={label}>
        {!state && <p className="muted small">{t.threadLoading}</p>}
        {state?.failed && (
          <p className="error small" role="alert">
            {t.threadError}
          </p>
        )}
        {state?.list && state.list.length === 0 && emptyText && (
          <p className="muted small">{emptyText}</p>
        )}
        {state?.list && state.list.length > 0 && (
          <ul className="rm-msgs">
            {state.list.map((c) => (
              <Message key={c.id} comment={c} actions={actions} now={now} />
            ))}
          </ul>
        )}
        <Composer
          label={proposalOnly ? t.proposeWrite : t.write}
          draft={drafts[key] ?? ''}
          sending={!!sending[key]}
          error={errors[key] ?? null}
          proposalOnly={proposalOnly}
          onDraft={(text) => setDrafts((all) => ({ ...all, [key]: text }))}
          onSend={(kind) => void send(target, kind)}
        />
      </div>
    );
  }

  const stages: RoadmapStage[] = page
    ? [
        ...page.stages.filter((s) => s.slug !== UNDER_THE_HOOD),
        ...page.stages.filter((s) => s.slug === UNDER_THE_HOOD),
      ]
    : [];
  const counted = stages.filter((s) => s.slug !== UNDER_THE_HOOD).flatMap((s) => s.items);
  const done = (items: RoadmapItem[]) => items.filter((i) => stateOf(i.state) === 'fet').length;
  const testing = (items: RoadmapItem[]) =>
    items.filter((i) => stateOf(i.state) === 'en-proves').length;
  const updated = roadmapUpdatedAt(page?.updatedAt);

  const adminTitles: Record<AdminList, { title: string; empty: string }> = {
    review: { title: t.admin.reviewTitle, empty: t.admin.reviewEmpty },
    unfiled: { title: t.admin.unfiledTitle, empty: t.admin.unfiledEmpty },
    retired: { title: t.admin.retiredTitle, empty: t.admin.retiredEmpty },
  };
  // A person reads the item's sentence and the stage's title; the id only when the file no longer has them.
  const whereOf = (c: RoadmapComment) => {
    const stages = page?.stages ?? [];
    if (c.itemId) {
      const item = stages.flatMap((s) => s.items).find((i) => i.id === c.itemId);
      return t.admin.where.item(item?.text ?? c.itemId);
    }
    if (c.stageSlug) {
      return t.admin.where.stage(stages.find((s) => s.slug === c.stageSlug)?.title ?? c.stageSlug);
    }
    return t.admin.where.general;
  };

  return (
    <Shell title={t.title} width="wide">
      <p className="muted">{t.intro}</p>
      <p className="small">
        <Link to="/que-ve">
          <i className="bi bi-hourglass-split" aria-hidden="true" /> {t.comingUp}
        </Link>
      </p>
      {page && (
        <p className="muted small" title={updated ? (page.updatedAt ?? undefined) : undefined}>
          {t.updated(updated, meta.version)}
        </p>
      )}
      {!failed && !page && <PageLoading />}
      {failed && !page && (
        <PageProblem error={pageError} text={t.error} onRetry={() => void loadPage()} />
      )}
      {page && page.stages.length === 0 && (
        <div className="empty">
          <i className="bi bi-signpost-split" aria-hidden="true" />
          <p className="muted">{t.empty}</p>
        </div>
      )}

      {page && admin && page.admin && (
        <section className="rm-adminbar rm-adminbox" aria-label={t.admin.only}>
          <span className="rm-only">
            <i className="bi bi-eye-slash" aria-hidden="true" /> {t.admin.only}
          </span>
          <div className="row">
            <button
              type="button"
              className={`chip toggle${adminList === 'review' ? ' on' : ''}`}
              aria-pressed={adminList === 'review'}
              onClick={() => toggleAdminList('review')}
            >
              <i className="bi bi-inbox" aria-hidden="true" /> {t.admin.review(page.admin.review)}
            </button>
            {(page.admin.unfiled > 0 || adminList === 'unfiled') && (
              <button
                type="button"
                className={`chip toggle${adminList === 'unfiled' ? ' on' : ''}`}
                aria-pressed={adminList === 'unfiled'}
                onClick={() => toggleAdminList('unfiled')}
              >
                <i className="bi bi-file-earmark-plus" aria-hidden="true" />{' '}
                {t.admin.unfiled(page.admin.unfiled)}
              </button>
            )}
            {(page.admin.retired > 0 || adminList === 'retired') && (
              <button
                type="button"
                className={`chip toggle${adminList === 'retired' ? ' on' : ''}`}
                aria-pressed={adminList === 'retired'}
                onClick={() => toggleAdminList('retired')}
              >
                <i className="bi bi-archive" aria-hidden="true" />{' '}
                {t.admin.retired(page.admin.retired)}
              </button>
            )}
          </div>
          {page.admin.problems.length > 0 && (
            <div className="muted small">
              <p>{t.admin.problems}</p>
              <ul>
                {page.admin.problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}
          {adminList && (
            <div className="rm-thread" role="region" aria-label={adminTitles[adminList].title}>
              <h2 className="section">{adminTitles[adminList].title}</h2>
              {!threads[adminList] && <p className="muted small">{t.threadLoading}</p>}
              {threads[adminList]?.failed && (
                <p className="error small" role="alert">
                  {t.threadError}
                </p>
              )}
              {threads[adminList]?.list?.length === 0 && (
                <p className="muted small">{adminTitles[adminList].empty}</p>
              )}
              <ul className="rm-msgs">
                {(threads[adminList]?.list ?? []).map((c) => (
                  <Message key={c.id} comment={c} actions={actions} now={now} where={whereOf(c)} />
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      {page && page.now.length > 0 && (
        <section className="card rm-now" aria-label={t.now}>
          <h2 className="section">{t.now}</h2>
          <div className="rm-now-cards">
            {page.now.map((card) => (
              <div key={card.title} className="rm-now-card">
                <strong>{card.title}</strong>
                {card.text && <p>{card.text}</p>}
              </div>
            ))}
          </div>
        </section>
      )}

      {page && page.figures.length > 0 && (
        <section className="rm-figures" aria-label={t.figures}>
          {page.figures.map((figure) => (
            <div key={`${figure.figure} ${figure.label}`} className="rm-figure">
              <strong>{figure.figure}</strong>
              <span className="muted small">{figure.label}</span>
            </div>
          ))}
        </section>
      )}

      {page && counted.length > 0 && (
        <section className="card rm-progress" aria-label={t.progressLabel}>
          <div className="row between">
            <strong>{t.progress(done(counted), counted.length, testing(counted))}</strong>
            <span className="rm-legend">
              {BAR_STATES.map((s) => (
                <span key={s}>
                  <i className={`rm-dot rm-${s}`} aria-hidden="true" /> {t.states[s]}
                </span>
              ))}
              <span>
                <i className="rm-dot rm-rest" aria-hidden="true" /> {t.rest}
              </span>
            </span>
          </div>
          <ProgressBar
            items={counted}
            label={t.progress(done(counted), counted.length, testing(counted))}
          />
        </section>
      )}

      {page && stages.length > 0 && (
        <nav className="rm-track" aria-label={t.track}>
          {stages.map((stage) => (
            <a key={stage.slug} href={`#etapa-${stage.slug}`} className="rm-track-stage">
              <span className={`rm-track-bar rm-${stateOf(stage.state)}`} aria-hidden="true" />
              <strong>{stage.title}</strong>
              <span className="muted small">{stage.when}</span>
            </a>
          ))}
        </nav>
      )}

      {stages.map((stage) => (
        <details
          key={stage.slug}
          id={`etapa-${stage.slug}`}
          className="card rm-stage"
          open={!!openStages[stage.slug]}
          onToggle={(e) => {
            const open = e.currentTarget.open;
            setOpenStages((all) =>
              all[stage.slug] === open ? all : { ...all, [stage.slug]: open },
            );
          }}
        >
          <summary>
            <span className="rm-stage-head">
              <h2 className="section">{stage.title}</h2>
              <span className="muted small">
                {t.stageSummary(stage.when, stage.items.length, done(stage.items))}
              </span>
              <ProgressBar
                items={stage.items}
                label={t.progress(done(stage.items), stage.items.length)}
              />
            </span>
            <StateChip state={stage.state} />
          </summary>
          <ul className="rm-items">
            {stage.items.map((item, index) => {
              const target = { item: item.id };
              const open = !!openThreads[keyOf(target)];
              const count = threads[keyOf(target)]?.list?.length ?? item.comments;
              return (
                <li
                  key={`${item.id}/${index}`}
                  id={item.id}
                  className={`rm-item${item.id === targetId ? ' target' : ''}`}
                  aria-current={item.id === targetId ? 'location' : undefined}
                >
                  <div className="rm-item-row">
                    <p className="rm-item-text">
                      {item.text}
                      {item.note && <span className="muted"> ({item.note})</span>}
                    </p>
                    <StateChip state={item.state} />
                    {(item.testedBy ?? 0) > 0 && (
                      <span
                        className="muted small rm-tested"
                        title={t.testedByTitle(item.testedBy ?? 0)}
                      >
                        <i className="bi bi-check2-circle" aria-hidden="true" />{' '}
                        {t.testedBy(item.testedBy ?? 0)}
                      </span>
                    )}
                    <button
                      type="button"
                      className={`ghost small rm-comment-toggle${count > 0 ? ' has' : ''}`}
                      aria-expanded={open}
                      onClick={() => toggleThread(target)}
                    >
                      <i className={`bi bi-chat${count > 0 ? '-dots' : ''}`} aria-hidden="true" />{' '}
                      {count > 0 ? t.comments(count) : t.comment}
                    </button>
                  </div>
                  {open && thread(target, t.threadOf(item.text), false, t.threadEmpty)}
                </li>
              );
            })}
          </ul>
          <div className="rm-stage-foot">
            <button
              type="button"
              className="ghost small"
              aria-expanded={!!openThreads[keyOf({ stage: stage.slug })]}
              onClick={() => toggleThread({ stage: stage.slug })}
            >
              <i className="bi bi-plus-lg" aria-hidden="true" /> {t.proposeForStage}
            </button>
            {(threads[keyOf({ stage: stage.slug })]?.list?.length ?? stage.proposals) > 0 && (
              <span className="muted small">
                {t.stageProposals(
                  threads[keyOf({ stage: stage.slug })]?.list?.length ?? stage.proposals,
                )}
              </span>
            )}
          </div>
          {openThreads[keyOf({ stage: stage.slug })] &&
            thread({ stage: stage.slug }, t.stageProposalsOf(stage.title), true)}
        </details>
      ))}

      {page && page.recipes.length > 0 && (
        <section className="card" aria-label={t.recipes}>
          <h2 className="section">{t.recipes}</h2>
          <p className="muted small">{t.recipesIntro}</p>
          <ul className="rm-recipes">
            {page.recipes.map((recipe) => (
              <li key={recipe.id}>
                <span>{recipe.title}</span>
                <span className={`chip rm-recipe-${recipe.status}`}>
                  {t.recipeStates[recipe.status] ?? t.recipeStates.coming}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {page && page.decisions.length > 0 && (
        <section className="card" aria-label={t.decisions}>
          <h2 className="section">{t.decisions}</h2>
          <ul className="rm-decisions">
            {page.decisions.map((decision, index) => (
              <li key={`${decision.date ?? ''}/${index}`}>
                {decision.date && (
                  <time className="muted small" dateTime={decision.date}>
                    {new Date(`${decision.date}T00:00:00`).toLocaleDateString('ca', {
                      dateStyle: 'medium',
                    })}
                  </time>
                )}
                <span>{decision.text}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {page && (
        <section className="card rm-idea" aria-label={t.idea}>
          <h2 className="section">{t.idea}</h2>
          <p className="muted small">{t.ideaIntro}</p>
          {thread({ general: true }, t.ideaList, true)}
        </section>
      )}
    </Shell>
  );
}
